import React from 'react'
import { ArrowLeft } from 'lucide-react'

interface PageHeaderProps {
  title: string
  subtitle?: string
  backLink?: {
    label?: string
    onClick: () => void
  }
  actions?: React.ReactNode[]
  showNotificationButton?: boolean
  tabs?: Array<{
    id: string
    label: string
  }>
  activeTab?: string
  onTabChange?: (tabId: string) => void
  companyName?: string
  lastUpdated?: string
  className?: string
}

export default function PageHeader({
  title,
  subtitle,
  backLink,
  actions = [],
  showNotificationButton = false,
  tabs,
  activeTab,
  onTabChange,
  companyName,
  lastUpdated,
  className = ""
}: PageHeaderProps) {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {/* Back Link */}
          {backLink && (
            <button
              onClick={backLink.onClick}
              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-500 hover:underline transition-colors mb-2"
            >
              <ArrowLeft className="w-3 h-3" />
              <span>{backLink.label || 'Back'}</span>
            </button>
          )}
          
          {/* Company Name (for detail pages) */}
          {companyName && (
            <div className="text-sm text-slate-600 dark:text-neutral-300 mb-1">
              {companyName}
            </div>
          )}
          
          {/* Main Title */}
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-neutral-50">
            {title}
          </h1>
          
          {/* Subtitle/Description */}
          {subtitle && (
            <p className="text-slate-600 dark:text-neutral-300 mt-1">
              {subtitle}
            </p>
          )}
          
          {/* Last Updated (for some pages) */}
          {lastUpdated && (
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {lastUpdated}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          {actions.map((action, index) => (
            <div key={index}>
              {action}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs Navigation */}
      {tabs && tabs.length > 0 && (
        <div className="border-b border-neutral-200 dark:border-neutral-600 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange && onTabChange(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </div>
  )
}